/**
 * Anotações manuais na mesa "Sobrepor & alinhar" (E36.F3.T03). // ux-neutrality:allow doc-comment
 *
 * Renderiza as anotações persistidas como objetos Fabric READ-ONLY // ux-neutrality:allow doc-comment
 * (selectable=false, evented=false) ANCORADOS à pose corrente da contestada: // ux-neutrality:allow doc-comment
 * a geometria normalizada (espaço da imagem) é reprojetada para o canvas via // ux-neutrality:allow doc-comment
 * matemática pura (lib/annotation_geometry.overlapShapeFor), então as marcas // ux-neutrality:allow doc-comment
 * acompanham mover/escalar/rotacionar em tempo real (object:moving/scaling/ // ux-neutrality:allow doc-comment
 * rotating) e ao assentar (object:modified). Desenhar/editar é do modo // ux-neutrality:allow doc-comment
 * Comparar — a mesa exibe; os gestos dela continuam da contestada. // ux-neutrality:allow doc-comment
 *
 * Módulo imperativo no padrão do OverlapViewport (testável com o mock de // ux-neutrality:allow doc-comment
 * Fabric do jsdom: lê a pose por propriedades planas, sem API de matriz). // ux-neutrality:allow doc-comment
 */
import * as fabric from 'fabric';
import { overlapShapeFor, type ContestedPose, type ImageSize } from '@/lib/annotation_geometry';
import { ANNOTATION_COLOR_HEX, type MesaAnnotationData } from '@/lib/mesa_annotations';

/** Subconjunto plano do FabricImage da contestada (mock-friendly). */
export interface ContestedObjectLike {
  width?: number;
  height?: number;
  left?: number;
  top?: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
}

export interface OverlapAnnotationsHandle {
  /** Substitui a lista renderizada e emite a vista serializada. */
  setAnnotations(list: MesaAnnotationData[]): void;
  /** Reprojeta da pose corrente (ex.: Resetar — não dispara object:modified). */
  sync(): void;
  dispose(): void;
}

const round2 = (v: number): number => Math.round(v * 100) / 100;

function poseOf(obj: ContestedObjectLike): { natural: ImageSize; pose: ContestedPose } | null {
  const width = obj.width ?? 0;
  const height = obj.height ?? 0;
  if (width <= 0 || height <= 0) return null;
  return {
    natural: { width, height },
    pose: {
      left: obj.left ?? 0,
      top: obj.top ?? 0,
      scaleX: obj.scaleX ?? 1,
      scaleY: obj.scaleY ?? 1,
      angle: obj.angle ?? 0,
    },
  };
}

export function wireOverlapAnnotations(
  canvas: fabric.Canvas,
  getContested: () => ContestedObjectLike | null,
  /** Vista serializada (canvas coords, arredondada) p/ data-attribute — o
   * canvas não dá DOM assertável (mesmo gotcha do data-transform, ADR-0040). */
  onViewChange?: (viewJson: string) => void,
): OverlapAnnotationsHandle {
  let annotations: MesaAnnotationData[] = [];
  let objects: fabric.FabricObject[] = [];

  function rebuild(emitView: boolean): void {
    for (const obj of objects) canvas.remove(obj);
    objects = [];
    const contested = getContested();
    const ctx = contested === null ? null : poseOf(contested);
    const views: object[] = [];
    if (ctx !== null) {
      for (const annotation of annotations) {
        const shape = overlapShapeFor(annotation, ctx.natural, ctx.pose);
        const color = ANNOTATION_COLOR_HEX[annotation.color];
        const inert = { selectable: false, evented: false };
        if (shape.kind === 'arrow') {
          const [tail, tip] = shape.shaft;
          objects.push(
            new fabric.Line([tail.x, tail.y, tip.x, tip.y], {
              stroke: color,
              strokeWidth: shape.strokeWidth,
              ...inert,
            }),
            new fabric.Polygon(shape.head, { fill: color, ...inert }),
          );
          views.push({
            id: annotation.id,
            tool: annotation.tool,
            color: annotation.color,
            points: [tail, tip].map((p) => [round2(p.x), round2(p.y)]),
          });
        } else {
          objects.push(
            new fabric.Circle({
              left: shape.cx,
              top: shape.cy,
              originX: 'center',
              originY: 'center',
              radius: shape.r,
              fill: 'transparent',
              stroke: color,
              strokeWidth: shape.strokeWidth,
              ...inert,
            }),
          );
          views.push({
            id: annotation.id,
            tool: annotation.tool,
            color: annotation.color,
            cx: round2(shape.cx),
            cy: round2(shape.cy),
            r: round2(shape.r),
          });
        }
      }
    }
    for (const obj of objects) canvas.add(obj);
    canvas.requestRenderAll();
    if (emitView) onViewChange?.(JSON.stringify(views));
  }

  // Gestos contínuos seguem sem serializar (custo por frame); o assentar e a
  // troca de lista emitem a vista para o data-attribute.
  const syncLive = (): void => rebuild(false);
  const syncSettled = (): void => rebuild(true);
  canvas.on('object:moving', syncLive);
  canvas.on('object:scaling', syncLive);
  canvas.on('object:rotating', syncLive);
  canvas.on('object:modified', syncSettled);

  return {
    setAnnotations(list: MesaAnnotationData[]): void {
      annotations = list;
      rebuild(true);
    },
    sync(): void {
      rebuild(true);
    },
    dispose(): void {
      canvas.off('object:moving', syncLive);
      canvas.off('object:scaling', syncLive);
      canvas.off('object:rotating', syncLive);
      canvas.off('object:modified', syncSettled);
      for (const obj of objects) canvas.remove(obj);
      objects = [];
    },
  };
}
